import type { ProxyUserDisconnectResponse } from '../api/proxyUsers';
import { disconnectCauseLabel, disconnectPlayerText, disconnectResponsibilityLabel } from './disconnectPresentation';

export const disconnectResponsibilityCounts = (report: ProxyUserDisconnectResponse) => {
  const counts = new Map<string, number>();
  for (const cause of report.causes) {
    counts.set(cause.responsibility, (counts.get(cause.responsibility) || 0) + cause.count);
  }
  return Array.from(counts, ([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
};

const countLine = (report: ProxyUserDisconnectResponse) =>
  disconnectResponsibilityCounts(report)
    .map(({ key, count }) => `${disconnectResponsibilityLabel(key)} ${count} 条`)
    .join('，');

export const buildProxyUserInternalReport = (name: string, report: ProxyUserDisconnectResponse) => {
  const lines = [
    `${name}｜近 ${report.window_hours} 小时游戏连接结束排查`,
    `截至 ${new Date(report.as_of).toLocaleString('zh-CN', { hour12: false })}，当前归属代理共记录 ${report.total} 条连接结束事件。`,
    report.total ? `归因线索：${countLine(report)}。` : '暂无可用的连接结束记录。',
  ];
  if (report.causes.length) {
    lines.push(`常见原因：${report.causes.slice(0, 5).map(cause => `${disconnectCauseLabel(cause.cause_code)} ${cause.count} 条`).join('，')}。`);
  }
  if (report.items.length) {
    lines.push('最近记录：');
    for (const item of report.items.slice(0, 10)) {
      const verdict = item.verdict_text ? `｜${item.verdict_text}` : '';
      lines.push(`- 记录于 ${item.recorded_at}｜${item.node_ip || '节点 IP 待解析'}（${item.node_name}）｜${item.role_name || item.account || '未识别角色'}｜${item.source_ip} → ${item.target_ip}:${item.target_port}｜${disconnectCauseLabel(item.cause_code)}（${disconnectResponsibilityLabel(item.responsibility)}，${item.confidence}）${verdict}`);
    }
  }
  lines.push('说明：记录时间为后台收到事件的时间，可能晚于实际断线；连接结束可能包括正常退出。审计仅保留近 24 小时、每代理最多 200 条。归属按代理当前所属用户计算。');
  return lines.join('\n');
};

/**
 * The text copied to a player. Plain language only: it quotes the player-facing
 * wording of each cause, never the technical verdict operators see.
 */
export const buildProxyUserCustomerReport = (name: string, report: ProxyUserDisconnectResponse) => {
  if (!report.total) {
    return `${name} 近 24 小时暂时没有可以核对的游戏连接记录，还不能判断连接是否正常。需要掉线的大致时间和角色名继续核对。`;
  }
  const drops = report.causes.filter(cause => disconnectPlayerText(cause.cause_code).counts);
  const dropCount = drops.reduce((sum, cause) => sum + cause.count, 0);
  const otherCount = Math.max(0, report.total - dropCount);

  const summary = dropCount
    ? `${name} 近 24 小时共有 ${report.total} 次游戏连接结束，其中 ${dropCount} 次是掉线${otherCount ? `，${otherCount} 次是玩家自己退出游戏或账号在其他位置登录（不算掉线）` : ''}。`
    : `${name} 近 24 小时共有 ${report.total} 次游戏连接结束，都是玩家自己退出游戏或账号在其他位置登录，没有发现掉线。`;

  const mainCauses = [...drops].sort((a, b) => b.count - a.count).slice(0, 3);
  const causeText = mainCauses.length
    ? `掉线的主要原因：${mainCauses.map(cause => `${disconnectPlayerText(cause.cause_code).label}（${cause.count} 次）`).join('、')}。`
    : '';

  const nodeIPs = Array.from(new Set(report.items.map(item => item.node_ip).filter(Boolean)));
  const ipText = nodeIPs.length ? `最近记录涉及的节点 IP：${nodeIPs.slice(0, 5).join('、')}${nodeIPs.length > 5 ? '等' : ''}。` : '';

  const latestDrop = report.items.find(item => disconnectPlayerText(item.cause_code).counts);
  const latest = latestDrop || report.items[0];
  const latestText = !latest ? ''
    : `最近一次${latestDrop ? '掉线' : '连接结束'}（记录时间 ${latest.recorded_at}）：${disconnectPlayerText(latest.cause_code).detail}`;

  return `${summary}${causeText}${ipText}${latestText}如果记录时间与实际情况对不上，需要掉线的大致时间和角色名继续核对。`;
};
